/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();
        this.stickyAtcInitialized = false;
      }

      connectedCallback() {
        super.connectedCallback();
        this.initializeStickyAtc();
      }

      initializeStickyAtc() {
        if (this.stickyAtcInitialized) return;

        this.section = this.closest('.section-main-product');
        if (!this.section) return;

        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        if (!this.mainProductForm || !this.buyButtons || !this.imageContainer) return;

        this.stickyAtcInitialized = true;
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.submitBtn?.addEventListener('click', () => {
          if (typeof window.gtag !== 'function') return;

          const productId = this.submitBtn.dataset.ga4ProductId || '';
          const variantId = this.submitBtn.dataset.ga4VariantId || this.form?.querySelector('[name="id"]')?.value || '';
          const itemId = `shopify_${window.Shopify?.country || 'US'}_${productId}_${variantId}`;
          const price = Number(this.submitBtn.dataset.ga4Price || 0);
          const quantity = 1;
          const currency = window.Shopify?.currency?.active || window.ga4Config?.currency || 'USD';

          window.gtag('event', 'add_to_cart_click', {
            add_to_cart_location: 'pdp_sticky',
            page_type: 'product',
            transport_type: 'beacon',
            ecomm_prodid: [itemId],
            ecomm_totalvalue: price * quantity,
            ecomm_pagetype: 'product',
            value: price * quantity,
            currency,
            id: itemId,
            name: this.submitBtn.dataset.ga4ProductName || '',
            brand: this.submitBtn.dataset.ga4Brand || '',
            category: this.submitBtn.dataset.ga4Category || '',
            price,
            quantity,
            variant: this.submitBtn.dataset.ga4VariantTitle || '',
            variant_id: variantId,
            sku: this.submitBtn.dataset.ga4Sku || ''
          });
        });

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
      }

      disconnectedCallback() {
        if (this.throttledOnScroll) {
          window.removeEventListener('scroll', this.throttledOnScroll);
        }
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantIdInput = this.section?.querySelector('product-form buy-buttons input[name="id"]');
        const variantId = variantIdInput?.value;
        if (!variantId) {
          const target = this.section?.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        if (!this.buyButtons) return;

        const stickyHeader = document.querySelector('.pageheader--sticky');
        const sectionHeader = document.querySelector('.section-header');
        const topOffset = stickyHeader && sectionHeader ? sectionHeader.offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');
        const variant = evt.detail.variant;

        if (variant) {
          if (this.variantTitle) this.variantTitle.textContent = variant.title;
          if (this.variantContainer) this.variantContainer.hidden = false;
          if (idInput) idInput.value = variant.id;
          if (this.submitBtn) {
            this.submitBtn.dataset.ga4VariantId = variant.id;
            this.submitBtn.dataset.ga4VariantTitle = variant.title || '';
            this.submitBtn.dataset.ga4Sku = variant.sku || '';
            this.submitBtn.dataset.ga4Price = (Number(variant.price || 0) / 100).toString();
          }
        } else {
          if (this.variantContainer) this.variantContainer.hidden = true;
          if (this.variantTitle) this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
          if (this.submitBtn) {
            this.submitBtn.dataset.ga4VariantId = '';
            this.submitBtn.dataset.ga4VariantTitle = '';
            this.submitBtn.dataset.ga4Sku = '';
            this.submitBtn.dataset.ga4Price = '0';
          }
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Updates the image.
       */
      updateImage() {
        if (!this.section || !this.imageContainer) return;

        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
